import React, { useState, useEffect } from 'react'

// 2026 Indianapolis 500 — May 24, 12:00 PM EDT (16:00 UTC)
const RACE_START = new Date('2026-05-24T16:00:00Z')

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
  total: number
}

function getTimeLeft(): TimeLeft | null {
  const total = RACE_START.getTime() - Date.now()
  if (total <= 0) return null
  return {
    total,
    days:    Math.floor(total / 86_400_000),
    hours:   Math.floor((total % 86_400_000) / 3_600_000),
    minutes: Math.floor((total % 3_600_000)  / 60_000),
    seconds: Math.floor((total % 60_000)     / 1_000),
  }
}

function pad(n: number) { return String(n).padStart(2, '0') }

// ── Large version (welcome screen) ────────────────────────────────────────
export function CountdownHero() {
  const [t, setT] = useState(getTimeLeft)
  useEffect(() => {
    const id = setInterval(() => setT(getTimeLeft()), 1000)
    return () => clearInterval(id)
  }, [])

  if (!t) return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.6rem',
      background: 'rgba(200,16,38,0.18)', border: '1px solid rgba(200,16,38,0.5)',
      borderRadius: 10, padding: '0.65rem 1.4rem',
      color: '#fff', fontFamily: 'Montserrat,sans-serif',
      fontSize: '1.05rem', fontWeight: 800, letterSpacing: 2,
    }}>
      🏁 RACE DAY — START YOUR ENGINES
    </div>
  )

  const units = t.days > 0
    ? [
        { val: t.days,    label: 'DAYS'    },
        { val: t.hours,   label: 'HOURS'   },
        { val: t.minutes, label: 'MINS'    },
        { val: t.seconds, label: 'SECS'    },
      ]
    : [
        { val: t.hours,   label: 'HOURS'   },
        { val: t.minutes, label: 'MINUTES' },
        { val: t.seconds, label: 'SECONDS' },
      ]

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '0.65rem', letterSpacing: 3, color: 'rgba(255,255,255,0.5)',
        fontFamily: 'Montserrat,sans-serif', fontWeight: 700,
        marginBottom: '0.5rem',
      }}>
        ⏱ RACE STARTS IN
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
        {units.map(({ val, label }) => (
          <div key={label} style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8, padding: '0.5rem 0.8rem', minWidth: 56,
          }}>
            <span style={{
              fontFamily: 'Montserrat,sans-serif', fontWeight: 900,
              fontSize: t.days > 0 ? '1.8rem' : '2rem',
              color: '#fff', lineHeight: 1,
              textShadow: '0 0 20px rgba(212,160,23,0.6)',
            }}>
              {pad(val)}
            </span>
            <span style={{
              fontSize: '0.55rem', letterSpacing: 1.5,
              color: 'rgba(255,255,255,0.4)', marginTop: '0.25rem',
              fontFamily: 'Montserrat,sans-serif', fontWeight: 700,
            }}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Compact version (roster / header strips) ──────────────────────────────
export function CountdownCompact() {
  const [t, setT] = useState(getTimeLeft)
  useEffect(() => {
    const id = setInterval(() => setT(getTimeLeft()), 1000)
    return () => clearInterval(id)
  }, [])

  if (!t) return (
    <div style={{
      background: 'rgba(200,16,38,0.15)', border: '1px solid rgba(200,16,38,0.4)',
      borderRadius: 6, padding: '0.25rem 0.75rem',
      fontSize: '0.75rem', fontWeight: 800, color: '#fff',
      fontFamily: 'Montserrat,sans-serif', letterSpacing: 1,
    }}>
      🏁 RACE DAY
    </div>
  )

  const label = t.days > 0
    ? `${t.days}d ${pad(t.hours)}h ${pad(t.minutes)}m ${pad(t.seconds)}s`
    : `${pad(t.hours)}:${pad(t.minutes)}:${pad(t.seconds)}`

  const isClose = t.total < 3_600_000   // under 1 hour — pulse red

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.4rem',
      background: isClose ? 'rgba(200,16,38,0.15)' : 'rgba(255,255,255,0.06)',
      border: `1px solid ${isClose ? 'rgba(200,16,38,0.45)' : 'rgba(255,255,255,0.12)'}`,
      borderRadius: 6, padding: '0.25rem 0.75rem',
      animation: isClose ? 'pulse 1s ease-in-out infinite' : undefined,
    }}>
      <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)',
        fontFamily: 'Montserrat,sans-serif', letterSpacing: 1 }}>
        ⏱ RACE IN
      </span>
      <span style={{
        fontSize: '0.82rem', fontWeight: 900, color: isClose ? '#f87171' : '#d4a017',
        fontFamily: 'Montserrat,sans-serif', letterSpacing: 0.5,
      }}>
        {label}
      </span>
    </div>
  )
}
